import {
  column,
  Css,
  emptyCell,
  GridColumn,
  GridDataRow,
  GridTable,
  MaxLines,
  simpleHeader,
  useTestIds,
} from "@homebound/beam";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { dateCell, tagCell } from "src/components";
import {
  BudgetSuperDrawer_ChangeEventFragment,
  BudgetSuperDrawer_ChangeEventLineItemFragment,
  BudgetSuperDrawer_ProjectItemFragment,
} from "src/routes/projects/budget/components/BudgetSuperDrawer.generated";
import { createCommitmentChangeOrderUrl, createCommitmentUrl } from "src/RouteUrls";
import {
  changeEventStatusToNameMapper,
  changeEventStatusToTagTypeMapper,
  changeEventTypeToNameMapper,
  formatCentsToPrice,
} from "src/utils";

export type BudgetChangeEventTableProps = {
  projectItems: BudgetSuperDrawer_ProjectItemFragment[];
  projectId: string;
  title: string;
};

export function BudgetChangeEventsTable(props: BudgetChangeEventTableProps) {
  const { projectItems, projectId, title } = props;
  const tid = useTestIds({});
  const changeEventLineItems = useMemo(() => projectItems.flatMap((pi) => pi.changeEventLineItems), [projectItems]);
  const rows = createRows(changeEventLineItems);
  const columns = createColumns(projectId, changeEventLineItems, tid);

  return (
    <div>
      <div css={Css.smSb.h3.mb2.$} data-testid="title">
        {title}
      </div>
      <GridTable id="changeEvents" columns={columns} rows={rows} />
    </div>
  );
}

function createRows(changeEventLineItems: BudgetSuperDrawer_ChangeEventLineItemFragment[]): GridDataRow<Row>[] {
  const changeEvents = changeEventLineItems.map((celi) => celi.changeEvent).unique();
  return [
    simpleHeader,
    ...changeEvents.map((ce) => ({ kind: "changeEvent" as const, id: ce.id, data: ce })),
    {
      kind: "totals" as const,
      id: "total",
      data: {
        changeEvents: changeEvents.sum((ce) => ce.totalCostInCents),
        perItems: changeEventLineItems.sum((celi) => celi.totalCostInCents),
      },
    },
  ];
}

function createColumns(
  projectId: string,
  changeEventLineItems: BudgetSuperDrawer_ChangeEventLineItemFragment[],
  tid: Record<string, object>,
): GridColumn<Row>[] {
  return [
    column<Row>({
      header: "Change Event",
      changeEvent: ({ name, blueprintUrl }) => <Link to={blueprintUrl.path}>{name}</Link>,
      totals: "Total",
      w: "200px",
    }),
    column<Row>({
      header: "Type",
      changeEvent: ({ type }) => changeEventTypeToNameMapper(type.code),
      totals: emptyCell,
      w: "100px",
    }),
    column<Row>({
      header: "Status",
      changeEvent: ({ status }) =>
        tagCell(changeEventStatusToTagTypeMapper[status], changeEventStatusToNameMapper[status]),
      totals: emptyCell,
      w: "100px",
    }),
    column<Row>({
      header: "Accept Date",
      changeEvent: ({ acceptedAt }) => dateCell(acceptedAt),
      totals: emptyCell,
      w: "100px",
    }),
    column<Row>({
      header: "Description",
      changeEvent: ({ internalNote }) => <MaxLines maxLines={3}>{internalNote}</MaxLines>,
      totals: emptyCell,
      w: "180px",
    }),
    column<Row>({
      header: "Reason for Change",
      changeEvent: ({ reason }) => reason.name,
      totals: emptyCell,
      w: "100px",
    }),
    column<Row>({
      header: "Cost Change",
      changeEvent: ({ totalCostInCents }) => formatCentsToPrice(totalCostInCents),
      totals: (data) => formatCentsToPrice(data.changeEvents),
      w: "100px",
    }),
    column<Row>({
      header: "Per Item Cost",
      changeEvent: ({ id }) =>
        formatCentsToPrice(
          // The total cost in change event line items for the current budget super drawer selection
          changeEventLineItems.filter((celi) => celi.changeEvent.id === id).sum((celi) => celi.totalCostInCents),
        ),
      totals: (data) => formatCentsToPrice(data.perItems),
      w: "100px",
    }),
    column<Row>({
      header: "Related Contracts",
      changeEvent: ({ homeownerContractChangeOrder, commitments }) => ({
        content: (
          <div css={Css.df.fdc.wsn.xs.$}>
            <MaxLines maxLines={3}>
              {homeownerContractChangeOrder?.name && (
                <div>
                  <Link to={homeownerContractChangeOrder.blueprintUrl.path}>{homeownerContractChangeOrder.name}</Link>
                  <br />
                </div>
              )}
              {commitments.map((cLike) => (
                <div key={cLike.name}>
                  {cLike.__typename === "CommitmentChangeOrder" && cLike.commitment.id ? (
                    <Link
                      {...tid.relatedContract}
                      to={createCommitmentChangeOrderUrl(projectId, cLike.commitment.id, cLike.id)}
                    >{`${cLike.shortName}-${cLike.displayName}`}</Link>
                  ) : (
                    <Link
                      {...tid.relatedContract}
                      to={createCommitmentUrl(projectId, cLike.id)}
                    >{`${cLike.shortName}-${cLike.displayName}`}</Link>
                  )}
                </div>
              ))}
            </MaxLines>
          </div>
        ),
      }),
      totals: emptyCell,
      w: "200px",
    }),
  ];
}

type Row = { kind: "header" } | ChangeEventRow | TotalRow;

type ChangeEventRow = { kind: "changeEvent"; id: string; data: BudgetSuperDrawer_ChangeEventFragment };

type TotalRow = { kind: "totals"; data: { changeEvents: number; perItems: number } };
